/**
 * String-based permission constants in "module:action" format.
 * Create/edit/delete merged into single `edit` action per module.
 * Invitation permissions removed — implied by `users:edit`.
 * Users and roles get `activate` instead of delete (row_status toggle).
 */
export const P = {
  PATIENTS_VIEW: 'patients:view',
  PATIENTS_EDIT: 'patients:edit',
  APPOINTMENTS_VIEW: 'appointments:view',
  APPOINTMENTS_EDIT: 'appointments:edit',
  PROVIDERS_VIEW: 'providers:view',
  PROVIDERS_EDIT: 'providers:edit',
  LOCATIONS_VIEW: 'locations:view',
  LOCATIONS_EDIT: 'locations:edit',
  TASKS_VIEW: 'tasks:view',
  TASKS_EDIT: 'tasks:edit',
  TASKS_ASSIGN: 'tasks:assign',
  USERS_VIEW: 'users:view',
  USERS_EDIT: 'users:edit',
  USERS_ACTIVATE: 'users:activate',
  EMAILS_VIEW: 'emails:view',
  INTEGRATIONS_VIEW: 'integrations:view',
  INTEGRATIONS_EDIT: 'integrations:edit',
  AGENTS_VIEW: 'agents:view',
  AGENTS_EDIT: 'agents:edit',
  TEAMS_VIEW: 'teams:view',
  TEAMS_EDIT: 'teams:edit',
  TEAMS_MANAGE_MEMBERS: 'teams:manage_members',
  WORKFLOWS_VIEW: 'workflows:view',
  WORKFLOWS_EDIT: 'workflows:edit',
  PROVIDER_GROUPS_VIEW: 'provider_groups:view',
  PROVIDER_GROUPS_EDIT: 'provider_groups:edit',
  ORGANIZATIONS_EDIT: 'organizations:edit',
  ENTITIES_VIEW: 'entities:view',
  ENTITIES_EDIT: 'entities:edit',
  DOCUMENTS_VIEW: 'documents:view',
  DOCUMENTS_EDIT: 'documents:edit',
  ROLES_VIEW: 'roles:view',
  ROLES_EDIT: 'roles:edit',
  ROLES_ACTIVATE: 'roles:activate',
  CLASSIFICATION_LABELS_VIEW: 'classification_labels:view',
  CLASSIFICATION_LABELS_EDIT: 'classification_labels:edit',
  ASSIGNMENT_RULES_VIEW: 'assignment_rules:view',
  ASSIGNMENT_RULES_EDIT: 'assignment_rules:edit',
  PAYORS_VIEW: 'payors:view',
  PAYORS_EDIT: 'payors:edit',
  OUTREACH_VIEW: 'outreach:view',
  OUTREACH_EDIT: 'outreach:edit',
  /** Start, pause, stop, or force a run. Operating, as distinct from authoring. */
  OUTREACH_RUN: 'outreach:run',
  /**
   * Listen to a call while it is happening.
   *
   * SEPARATE FROM `patients:view` ON PURPOSE. Reading a finished call's
   * transcript and hearing a patient speak in real time are different
   * exposures, and the second one is the kind a practice wants to hand to a
   * named supervisor rather than to everyone who can open a chart. Listening
   * is subscribe-only and cannot be heard by the caller; the risk it carries
   * is disclosure, not interference.
   */
  CALLS_LISTEN: 'calls:listen',
} as const;

export type PermissionString = (typeof P)[keyof typeof P];

/** All permission strings as an array — used by seeders. */
export const ALL_PERMISSIONS: PermissionString[] = Object.values(P);

/** Bump this whenever permissions or system role templates change. */
export const PERMISSION_VERSION = 12;

/**
 * Module permission metadata for the role editor UI.
 * Groups permissions by module with labels and scopeable flag.
 */
export interface ModulePermission {
  permission: PermissionString;
  label: string;
  scopeable: boolean;
}

export interface PermissionModule {
  module: string;
  label: string;
  permissions: ModulePermission[];
}

export const MODULE_PERMISSIONS: PermissionModule[] = [
  {
    module: 'calls',
    label: 'Calls',
    permissions: [{ permission: P.CALLS_LISTEN, label: 'Listen to live calls', scopeable: false }],
  },
  {
    module: 'patients',
    label: 'Patients',
    permissions: [
      { permission: P.PATIENTS_VIEW, label: 'View patients', scopeable: true },
      { permission: P.PATIENTS_EDIT, label: 'Create & edit patients', scopeable: true },
    ],
  },
  {
    module: 'appointments',
    label: 'Appointments',
    permissions: [
      { permission: P.APPOINTMENTS_VIEW, label: 'View appointments', scopeable: true },
      { permission: P.APPOINTMENTS_EDIT, label: 'Create & edit appointments', scopeable: true },
    ],
  },
  {
    module: 'providers',
    label: 'Providers',
    permissions: [
      { permission: P.PROVIDERS_VIEW, label: 'View providers', scopeable: false },
      { permission: P.PROVIDERS_EDIT, label: 'Create & edit providers', scopeable: false },
    ],
  },
  {
    module: 'locations',
    label: 'Locations',
    permissions: [
      { permission: P.LOCATIONS_VIEW, label: 'View locations', scopeable: false },
      { permission: P.LOCATIONS_EDIT, label: 'Create & edit locations', scopeable: false },
    ],
  },
  {
    module: 'tasks',
    label: 'Tasks',
    permissions: [
      { permission: P.TASKS_VIEW, label: 'View tasks', scopeable: true },
      { permission: P.TASKS_EDIT, label: 'Create & edit tasks', scopeable: true },
      { permission: P.TASKS_ASSIGN, label: 'Assign tasks', scopeable: true },
    ],
  },
  {
    module: 'users',
    label: 'Users & Invitations',
    permissions: [
      { permission: P.USERS_VIEW, label: 'View users & invitations', scopeable: true },
      { permission: P.USERS_EDIT, label: 'Create & edit users, send invitations', scopeable: true },
      { permission: P.USERS_ACTIVATE, label: 'Activate / deactivate users', scopeable: false },
    ],
  },
  {
    module: 'emails',
    label: 'Emails',
    permissions: [{ permission: P.EMAILS_VIEW, label: 'View emails', scopeable: false }],
  },
  {
    module: 'integrations',
    label: 'Integrations',
    permissions: [
      { permission: P.INTEGRATIONS_VIEW, label: 'View integrations', scopeable: false },
      { permission: P.INTEGRATIONS_EDIT, label: 'Create & edit integrations', scopeable: false },
    ],
  },
  {
    module: 'entities',
    label: 'Entities',
    permissions: [
      { permission: P.ENTITIES_VIEW, label: 'View entities', scopeable: false },
      { permission: P.ENTITIES_EDIT, label: 'Create & edit entities', scopeable: false },
    ],
  },
  {
    module: 'documents',
    label: 'Documents',
    permissions: [
      { permission: P.DOCUMENTS_VIEW, label: 'View documents', scopeable: false },
      { permission: P.DOCUMENTS_EDIT, label: 'Edit & review documents', scopeable: false },
    ],
  },
  {
    module: 'agents',
    label: 'AI Agents',
    permissions: [
      { permission: P.AGENTS_VIEW, label: 'View agents', scopeable: false },
      { permission: P.AGENTS_EDIT, label: 'Create & edit agents', scopeable: false },
    ],
  },
  {
    module: 'teams',
    label: 'Teams',
    permissions: [
      { permission: P.TEAMS_VIEW, label: 'View teams', scopeable: false },
      { permission: P.TEAMS_EDIT, label: 'Create & edit teams', scopeable: false },
      { permission: P.TEAMS_MANAGE_MEMBERS, label: 'Manage team members', scopeable: false },
    ],
  },
  {
    module: 'workflows',
    label: 'Workflows',
    permissions: [
      { permission: P.WORKFLOWS_VIEW, label: 'View workflows', scopeable: false },
      { permission: P.WORKFLOWS_EDIT, label: 'Create & edit workflows', scopeable: false },
    ],
  },
  {
    module: 'provider_groups',
    label: 'Provider Groups',
    permissions: [
      { permission: P.PROVIDER_GROUPS_VIEW, label: 'View provider groups', scopeable: false },
      {
        permission: P.PROVIDER_GROUPS_EDIT,
        label: 'Create & edit provider groups',
        scopeable: false,
      },
    ],
  },
  {
    module: 'organizations',
    label: 'Organization',
    permissions: [
      { permission: P.ORGANIZATIONS_EDIT, label: 'Edit organization settings', scopeable: false },
    ],
  },
  {
    module: 'roles',
    label: 'Roles',
    permissions: [
      { permission: P.ROLES_VIEW, label: 'View roles', scopeable: false },
      { permission: P.ROLES_EDIT, label: 'Create & edit roles', scopeable: false },
      { permission: P.ROLES_ACTIVATE, label: 'Activate / deactivate roles', scopeable: false },
    ],
  },
  {
    module: 'classification_labels',
    label: 'Classification Labels',
    permissions: [
      {
        permission: P.CLASSIFICATION_LABELS_VIEW,
        label: 'View classification labels',
        scopeable: false,
      },
      {
        permission: P.CLASSIFICATION_LABELS_EDIT,
        label: 'Create & edit classification labels',
        scopeable: false,
      },
    ],
  },
  {
    module: 'assignment_rules',
    label: 'Assignment Rules',
    permissions: [
      {
        permission: P.ASSIGNMENT_RULES_VIEW,
        label: 'View assignment rules',
        scopeable: false,
      },
      {
        permission: P.ASSIGNMENT_RULES_EDIT,
        label: 'Create & edit assignment rules',
        scopeable: false,
      },
    ],
  },
  {
    module: 'payors',
    label: 'Payors',
    permissions: [
      { permission: P.PAYORS_VIEW, label: 'View payors & accepted plans', scopeable: false },
      {
        permission: P.PAYORS_EDIT,
        label: 'Manage accepted payors',
        scopeable: false,
      },
    ],
  },
  {
    module: 'outreach',
    label: 'Outreach',
    permissions: [
      { permission: P.OUTREACH_VIEW, label: 'View campaigns & runs', scopeable: false },
      {
        // Also governs the contact policy, which is the widest blast radius in
        // the system: it caps every campaign at once. Deliberately not a
        // separate permission — see ADR-154 §15 D3, which is open.
        permission: P.OUTREACH_EDIT,
        label: 'Author campaigns & set the contact policy',
        scopeable: false,
      },
      { permission: P.OUTREACH_RUN, label: 'Pause, stop, or run a patient now', scopeable: false },
    ],
  },
];

/**
 * System role templates.
 * Define which permissions each system role gets by default.
 * Scope: 'all' = can see/edit everything; 'own' = only own items.
 */
export interface SystemRolePermission {
  permission: PermissionString;
  scope: 'all' | 'own' | null;
}

export interface SystemRoleTemplate {
  slug: string;
  name: string;
  description: string;
  isSystemAdmin: boolean;
  permissions: SystemRolePermission[];
}

export const SYSTEM_ROLE_TEMPLATES: SystemRoleTemplate[] = [
  {
    slug: 'admin',
    name: 'Admin',
    description: 'Full access to all features. System admins bypass permission checks.',
    isSystemAdmin: true,
    permissions: ALL_PERMISSIONS.map((p) => ({ permission: p, scope: 'all' as const })),
  },
  {
    slug: 'manager',
    name: 'Manager',
    description: 'Can manage most features with full scope.',
    isSystemAdmin: false,
    permissions: [
      { permission: P.CALLS_LISTEN, scope: null },
      { permission: P.PATIENTS_VIEW, scope: 'all' },
      { permission: P.PATIENTS_EDIT, scope: 'all' },
      { permission: P.APPOINTMENTS_VIEW, scope: 'all' },
      { permission: P.APPOINTMENTS_EDIT, scope: 'all' },
      { permission: P.PROVIDERS_VIEW, scope: null },
      { permission: P.PROVIDERS_EDIT, scope: null },
      { permission: P.LOCATIONS_VIEW, scope: null },
      { permission: P.LOCATIONS_EDIT, scope: null },
      { permission: P.TASKS_VIEW, scope: 'all' },
      { permission: P.TASKS_EDIT, scope: 'all' },
      { permission: P.TASKS_ASSIGN, scope: 'all' },
      { permission: P.USERS_VIEW, scope: 'all' },
      { permission: P.USERS_EDIT, scope: 'all' },
      { permission: P.EMAILS_VIEW, scope: null },
      { permission: P.ENTITIES_VIEW, scope: null },
      { permission: P.ENTITIES_EDIT, scope: null },
      { permission: P.DOCUMENTS_VIEW, scope: null },
      { permission: P.DOCUMENTS_EDIT, scope: null },
      { permission: P.INTEGRATIONS_VIEW, scope: null },
      { permission: P.INTEGRATIONS_EDIT, scope: null },
      { permission: P.AGENTS_VIEW, scope: null },
      { permission: P.AGENTS_EDIT, scope: null },
      { permission: P.TEAMS_VIEW, scope: null },
      { permission: P.TEAMS_EDIT, scope: null },
      { permission: P.TEAMS_MANAGE_MEMBERS, scope: null },
      { permission: P.WORKFLOWS_VIEW, scope: null },
      { permission: P.WORKFLOWS_EDIT, scope: null },
      { permission: P.PROVIDER_GROUPS_VIEW, scope: null },
      { permission: P.PROVIDER_GROUPS_EDIT, scope: null },
      { permission: P.CLASSIFICATION_LABELS_VIEW, scope: null },
      { permission: P.CLASSIFICATION_LABELS_EDIT, scope: null },
      { permission: P.ASSIGNMENT_RULES_VIEW, scope: null },
      { permission: P.ASSIGNMENT_RULES_EDIT, scope: null },
      { permission: P.PAYORS_VIEW, scope: null },
      { permission: P.PAYORS_EDIT, scope: null },
      { permission: P.OUTREACH_VIEW, scope: null },
      { permission: P.OUTREACH_EDIT, scope: null },
      { permission: P.OUTREACH_RUN, scope: null },
    ],
  },
  {
    slug: 'staff',
    name: 'Staff',
    description:
      'View access to most features plus full task and document management (edit, assign, mark done).',
    isSystemAdmin: false,
    permissions: [
      { permission: P.PATIENTS_VIEW, scope: 'all' },
      { permission: P.APPOINTMENTS_VIEW, scope: 'all' },
      { permission: P.PROVIDERS_VIEW, scope: null },
      { permission: P.LOCATIONS_VIEW, scope: null },
      { permission: P.TASKS_VIEW, scope: 'all' },
      { permission: P.TASKS_EDIT, scope: 'all' },
      { permission: P.TASKS_ASSIGN, scope: 'all' },
      { permission: P.USERS_VIEW, scope: 'all' },
      { permission: P.EMAILS_VIEW, scope: null },
      { permission: P.ENTITIES_VIEW, scope: null },
      { permission: P.DOCUMENTS_VIEW, scope: null },
      { permission: P.DOCUMENTS_EDIT, scope: null },
      { permission: P.AGENTS_VIEW, scope: null },
      { permission: P.TEAMS_VIEW, scope: null },
      { permission: P.WORKFLOWS_VIEW, scope: null },
      { permission: P.PROVIDER_GROUPS_VIEW, scope: null },
      { permission: P.CLASSIFICATION_LABELS_VIEW, scope: null },
      { permission: P.ASSIGNMENT_RULES_VIEW, scope: null },
      { permission: P.PAYORS_VIEW, scope: null },
    ],
  },
];

// ---------------------------------------------------------------------------
// Helper functions — new signature uses RequestHeader
// ---------------------------------------------------------------------------

/**
 * Check if user has a specific permission.
 * System admins bypass all permission checks.
 */
export function hasPermission(
  header: { isSystemAdmin: boolean; permissions: Set<string> },
  perm: PermissionString
): boolean {
  return header.isSystemAdmin || header.permissions.has(perm);
}

/**
 * Check if user has all specified permissions.
 */
export function hasAllPermissions(
  header: { isSystemAdmin: boolean; permissions: Set<string> },
  required: PermissionString[]
): boolean {
  if (header.isSystemAdmin) return true;
  return required.every((p) => header.permissions.has(p));
}

/**
 * Check if user has any of the specified permissions.
 */
export function hasAnyPermission(
  header: { isSystemAdmin: boolean; permissions: Set<string> },
  required: PermissionString[]
): boolean {
  if (header.isSystemAdmin) return true;
  return required.some((p) => header.permissions.has(p));
}

/**
 * Check if user has a permission with a specific scope.
 */
export function hasPermissionWithScope(
  header: { isSystemAdmin: boolean; permissions: Set<string>; scopes: Map<string, string> },
  perm: PermissionString,
  requiredScope: 'all' | 'own'
): boolean {
  if (header.isSystemAdmin) return true;
  if (!header.permissions.has(perm)) return false;
  if (requiredScope === 'own') return true; // 'all' scope also satisfies 'own' requirement
  const scope = header.scopes.get(perm);
  return scope === 'all';
}

/**
 * Check if user is a system admin.
 */
export function isAdmin(header: { isSystemAdmin: boolean }): boolean {
  return header.isSystemAdmin;
}

/**
 * Parse a permission string into module and action.
 */
export function parsePermission(perm: PermissionString): { module: string; action: string } {
  const parts = perm.split(':');
  return { module: parts[0]!, action: parts[1]! };
}
